/**
 * Leviathan Web - Dispatch Detail Page
 * Quasar Prime LLC
 * View and manage individual dispatch with status workflow
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Truck,
  User,
  Clock,
  MapPin,
  Package,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Play,
  Navigation,
  Flag,
  FileText,
  Phone,
  Trash2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface Dispatch {
  id: string;
  dispatchNumber: string;
  status: string;
  scheduledDate: string;
  scheduledTime: string | null;
  estimatedDuration: number | null;
  departedAt: string | null;
  arrivedAt: string | null;
  completedAt: string | null;
  actualQuantity: number | null;
  signature: string | null;
  signedBy: string | null;
  startLocation: string | null;
  currentLocation: string | null;
  endLocation: string | null;
  dispatchNotes: string | null;
  deliveryNotes: string | null;
  createdAt: string;
  order: {
    id: string;
    orderNumber: string;
    total: number;
    priority: string;
    deliveryAddress: string | null;
    deliveryCity: string | null;
    deliveryState: string | null;
    deliveryZip: string | null;
    deliveryInstructions: string | null;
    customer: {
      id: string;
      name: string;
      customerNumber: string;
      phone: string | null;
    };
    items: Array<{
      id: string;
      quantity: number;
      unitPrice: number;
      lineTotal: number;
      product: {
        id: string;
        name: string;
        sku: string;
        unit: string;
      };
    }>;
  };
  truck: {
    id: string;
    unitNumber: string;
    name: string | null;
    totalCapacity: number | null;
    make: string | null;
    model: string | null;
    licensePlate: string | null;
  };
  driver: {
    id: string;
    driverNumber: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    hazmatCertified: boolean;
    tankerEndorsement: boolean;
    twicCard: boolean;
  };
}

const STATUS_CONFIG: Record<string, {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ElementType;
  nextStatus: string | null;
  nextAction: string | null;
}> = {
  SCHEDULED: {
    label: 'Scheduled',
    color: 'text-steel',
    bgColor: 'bg-steel/20 border-steel/50',
    icon: Clock,
    nextStatus: 'EN_ROUTE',
    nextAction: 'Start Delivery',
  },
  EN_ROUTE: {
    label: 'En Route',
    color: 'text-amber',
    bgColor: 'bg-amber/20 border-amber/50',
    icon: Navigation,
    nextStatus: 'ARRIVED',
    nextAction: 'Mark Arrived',
  },
  ARRIVED: {
    label: 'Arrived',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-400/20 border-cyan-400/50',
    icon: MapPin,
    nextStatus: 'DELIVERING',
    nextAction: 'Begin Delivery',
  },
  DELIVERING: {
    label: 'Delivering',
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/20 border-purple-400/50',
    icon: Package,
    nextStatus: 'COMPLETED',
    nextAction: 'Complete Delivery',
  },
  COMPLETED: {
    label: 'Completed',
    color: 'text-success-green',
    bgColor: 'bg-success-green/20 border-success-green/50',
    icon: CheckCircle,
    nextStatus: null,
    nextAction: null,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-text-tertiary',
    bgColor: 'bg-panel-border/20 border-panel-border/50',
    icon: XCircle,
    nextStatus: null,
    nextAction: null,
  },
  FAILED: {
    label: 'Failed',
    color: 'text-error-red',
    bgColor: 'bg-error-red/20 border-error-red/50',
    icon: AlertTriangle,
    nextStatus: 'SCHEDULED',
    nextAction: 'Reschedule',
  },
};

const DispatchDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [dispatch, setDispatch] = useState<Dispatch | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Completion form state
  const [actualQuantity, setActualQuantity] = useState('');
  const [signedBy, setSignedBy] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchDispatch();
  }, [id, accessToken]);

  const fetchDispatch = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dispatch/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setDispatch(data);
      } else {
        setError('Dispatch not found');
      }
    } catch (err) {
      setError('Failed to load dispatch');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!dispatch) return;

    setIsUpdating(true);
    try {
      const payload: any = { status: newStatus };

      if (newStatus === 'COMPLETED') {
        payload.actualQuantity = actualQuantity || null;
        payload.signedBy = signedBy || null;
        payload.deliveryNotes = deliveryNotes || null;
      }

      if (newStatus === 'CANCELLED' || newStatus === 'FAILED') {
        payload.deliveryNotes = cancelReason || deliveryNotes || null;
      }

      const response = await fetch(`${API_URL}/api/dispatch/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update status');
      }

      await fetchDispatch();
      setShowStatusModal(false);
      setShowCancelModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dispatch/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete dispatch');
      }

      navigate('/dispatch');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete dispatch');
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center space-x-4">
          <div className="w-3 h-3 bg-amber animate-pulse" />
          <span className="text-text-secondary font-mono">Loading dispatch...</span>
        </div>
      </div>
    );
  }

  if (error || !dispatch) {
    return (
      <div className="text-center py-20">
        <AlertTriangle className="w-16 h-16 text-error-red mx-auto mb-4" />
        <p className="text-error-red font-mono mb-4">{error || 'Dispatch not found'}</p>
        <Link to="/dispatch" className="text-amber font-mono hover:underline">
          &larr; Back to Dispatch Board
        </Link>
      </div>
    );
  }

  const statusConfig = (STATUS_CONFIG[dispatch.status] || STATUS_CONFIG.SCHEDULED)!;
  const StatusIcon = statusConfig.icon;
  const totalOrderedQuantity = dispatch.order.items.reduce((sum, item) => sum + item.quantity, 0);
  const canCancel = ['SCHEDULED', 'EN_ROUTE', 'ARRIVED', 'DELIVERING'].includes(dispatch.status);
  const canDelete = ['SCHEDULED', 'CANCELLED'].includes(dispatch.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4 mb-2">
                <Link
                  to="/dispatch"
                  className="text-text-secondary hover:text-amber transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Link>
                <span
                  className={`px-3 py-1 text-sm font-mono border ${statusConfig.bgColor} ${statusConfig.color}`}
                  style={{ textShadow: '0 0 8px currentColor' }}
                >
                  <StatusIcon className="w-4 h-4 inline mr-2" />
                  {statusConfig.label}
                </span>
              </div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {dispatch.dispatchNumber}
              </h1>
              <p className="text-text-secondary text-sm mt-1 font-mono">
                Scheduled: {new Date(dispatch.scheduledDate).toLocaleDateString()}
                {dispatch.scheduledTime && ` at ${dispatch.scheduledTime}`}
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {canDelete && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-error-red/20 hover:bg-error-red/30 text-error-red border border-error-red/50 transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">Delete</span>
                </button>
              )}
              {canCancel && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-panel-border text-text-secondary border border-panel-border transition-all"
                >
                  <XCircle className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">Cancel</span>
                </button>
              )}
              {statusConfig.nextAction && (
                <button
                  onClick={() => {
                    if (statusConfig.nextStatus === 'COMPLETED') {
                      setShowStatusModal(true);
                    } else {
                      handleStatusUpdate(statusConfig.nextStatus!);
                    }
                  }}
                  disabled={isUpdating}
                  className="flex items-center space-x-2 px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                  style={{ boxShadow: '0 0 15px rgba(255, 149, 0, 0.4)' }}
                >
                  <Play className="w-5 h-5" />
                  <span>{statusConfig.nextAction}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Timeline */}
      <div className="bg-panel-dark border border-panel-border p-6">
        <h3 className="text-sm font-mono text-text-tertiary uppercase tracking-wider mb-4">
          Delivery Progress
        </h3>
        <div className="flex items-center justify-between">
          {['SCHEDULED', 'EN_ROUTE', 'ARRIVED', 'DELIVERING', 'COMPLETED'].map((status, index) => {
            const config = STATUS_CONFIG[status]!;
            const Icon = config.icon;
            const isActive = dispatch.status === status;
            const isPast = ['SCHEDULED', 'EN_ROUTE', 'ARRIVED', 'DELIVERING', 'COMPLETED'].indexOf(dispatch.status) > index;
            const isFailed = dispatch.status === 'FAILED' || dispatch.status === 'CANCELLED';

            return (
              <div key={status} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                      isActive
                        ? `${config.bgColor} ${config.color} border-current`
                        : isPast
                        ? 'bg-success-green/20 border-success-green text-success-green'
                        : isFailed
                        ? 'bg-panel-border/20 border-panel-border text-text-tertiary'
                        : 'bg-panel-border/20 border-panel-border text-text-tertiary'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className={`text-xs font-mono mt-2 ${
                      isActive ? config.color : isPast ? 'text-success-green' : 'text-text-tertiary'
                    }`}
                  >
                    {config.label}
                  </span>
                </div>
                {index < 4 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      isPast ? 'bg-success-green' : 'bg-panel-border'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Details */}
        <div className="lg:col-span-2 space-y-6">
          <InfoPanel title="Order Details" icon={Package}>
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-panel-border">
              <div>
                <p className="text-amber font-mono">{dispatch.order.orderNumber}</p>
                <p className="text-text-primary text-lg">{dispatch.order.customer.name}</p>
                <p className="text-text-tertiary text-sm font-mono">
                  {dispatch.order.customer.customerNumber}
                </p>
              </div>
              {dispatch.order.customer.phone && (
                <a
                  href={`tel:${dispatch.order.customer.phone}`}
                  className="flex items-center space-x-2 text-steel hover:text-amber transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-mono">{dispatch.order.customer.phone}</span>
                </a>
              )}
            </div>

            {/* Delivery Address */}
            <div className="mb-4">
              <p className="text-text-tertiary text-xs uppercase font-mono mb-1">Delivery Address</p>
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-amber mt-1" />
                <div>
                  <p className="text-text-primary">{dispatch.order.deliveryAddress}</p>
                  <p className="text-text-secondary">
                    {dispatch.order.deliveryCity}, {dispatch.order.deliveryState}{' '}
                    {dispatch.order.deliveryZip}
                  </p>
                </div>
              </div>
            </div>

            {dispatch.order.deliveryInstructions && (
              <div className="mb-4 p-3 bg-amber/10 border border-amber/30">
                <p className="text-amber text-xs uppercase font-mono mb-1">Delivery Instructions</p>
                <p className="text-text-primary text-sm">{dispatch.order.deliveryInstructions}</p>
              </div>
            )}

            {/* Order Items */}
            <div className="border-t border-panel-border pt-4">
              <p className="text-text-tertiary text-xs uppercase font-mono mb-3">Order Items</p>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-text-tertiary text-xs uppercase font-mono">
                    <th className="pb-2">Product</th>
                    <th className="pb-2 text-right">Quantity</th>
                    <th className="pb-2 text-right">Price</th>
                    <th className="pb-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatch.order.items.map((item) => (
                    <tr key={item.id} className="border-t border-panel-border">
                      <td className="py-2">
                        <p className="text-text-primary">{item.product.name}</p>
                        <p className="text-text-tertiary text-xs font-mono">{item.product.sku}</p>
                      </td>
                      <td className="py-2 text-right text-text-primary font-mono">
                        {item.quantity.toLocaleString()} {item.product.unit}
                      </td>
                      <td className="py-2 text-right text-text-secondary font-mono">
                        ${item.unitPrice.toFixed(2)}
                      </td>
                      <td className="py-2 text-right text-text-primary font-mono">
                        ${item.lineTotal.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-amber">
                    <td colSpan={3} className="py-2 text-right text-text-tertiary font-mono">
                      Order Total
                    </td>
                    <td className="py-2 text-right text-amber font-display text-lg">
                      ${dispatch.order.total.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </InfoPanel>

          {/* Delivery Execution */}
          {(dispatch.departedAt || dispatch.arrivedAt || dispatch.completedAt) && (
            <InfoPanel title="Delivery Execution" icon={Flag}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <TimeStampCard label="Departed" time={dispatch.departedAt} />
                <TimeStampCard label="Arrived" time={dispatch.arrivedAt} />
                <TimeStampCard label="Completed" time={dispatch.completedAt} />
                {dispatch.actualQuantity && (
                  <div>
                    <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                      Delivered
                    </p>
                    <p className="text-text-primary font-mono text-lg">
                      {dispatch.actualQuantity.toLocaleString()} gal
                    </p>
                    {dispatch.actualQuantity !== totalOrderedQuantity && (
                      <p
                        className={`text-xs font-mono ${
                          dispatch.actualQuantity < totalOrderedQuantity
                            ? 'text-error-red'
                            : 'text-success-green'
                        }`}
                      >
                        {dispatch.actualQuantity > totalOrderedQuantity ? '+' : ''}
                        {(dispatch.actualQuantity - totalOrderedQuantity).toLocaleString()} gal
                      </p>
                    )}
                  </div>
                )}
              </div>
              {dispatch.signedBy && (
                <div className="mt-4 pt-4 border-t border-panel-border">
                  <p className="text-text-tertiary text-xs uppercase font-mono mb-1">Signed By</p>
                  <p className="text-text-primary">{dispatch.signedBy}</p>
                </div>
              )}
              {dispatch.deliveryNotes && (
                <div className="mt-4 pt-4 border-t border-panel-border">
                  <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                    Delivery Notes
                  </p>
                  <p className="text-text-secondary">{dispatch.deliveryNotes}</p>
                </div>
              )}
            </InfoPanel>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Truck Info */}
          <InfoPanel title="Assigned Truck" icon={Truck}>
            <div className="text-center py-4">
              <Truck className="w-16 h-16 mx-auto text-amber mb-4" />
              <p className="text-amber font-mono text-xl">{dispatch.truck.unitNumber}</p>
              {dispatch.truck.name && (
                <p className="text-text-primary">{dispatch.truck.name}</p>
              )}
              <div className="mt-4 text-text-secondary text-sm space-y-1">
                {dispatch.truck.make && dispatch.truck.model && (
                  <p>
                    {dispatch.truck.make} {dispatch.truck.model}
                  </p>
                )}
                {dispatch.truck.licensePlate && (
                  <p className="font-mono">{dispatch.truck.licensePlate}</p>
                )}
                {dispatch.truck.totalCapacity && (
                  <p className="text-amber">
                    {dispatch.truck.totalCapacity.toLocaleString()} gal capacity
                  </p>
                )}
              </div>
            </div>
          </InfoPanel>

          {/* Driver Info */}
          <InfoPanel title="Assigned Driver" icon={User}>
            <div className="text-center py-4">
              <User className="w-16 h-16 mx-auto text-steel mb-4" />
              <p className="text-steel font-mono text-xl">{dispatch.driver.driverNumber}</p>
              <p className="text-text-primary text-lg">
                {dispatch.driver.firstName} {dispatch.driver.lastName}
              </p>
              {dispatch.driver.phone && (
                <a
                  href={`tel:${dispatch.driver.phone}`}
                  className="flex items-center justify-center space-x-2 text-amber hover:underline mt-2"
                >
                  <Phone className="w-4 h-4" />
                  <span className="font-mono">{dispatch.driver.phone}</span>
                </a>
              )}
              <div className="flex justify-center space-x-2 mt-4">
                {dispatch.driver.hazmatCertified && (
                  <span className="px-2 py-1 text-xs font-mono bg-error-red/20 text-error-red border border-error-red/50">
                    HAZMAT
                  </span>
                )}
                {dispatch.driver.tankerEndorsement && (
                  <span className="px-2 py-1 text-xs font-mono bg-steel/20 text-steel border border-steel/50">
                    TANKER
                  </span>
                )}
                {dispatch.driver.twicCard && (
                  <span className="px-2 py-1 text-xs font-mono bg-amber/20 text-amber border border-amber/50">
                    TWIC
                  </span>
                )}
              </div>
            </div>
          </InfoPanel>

          {/* Dispatch Notes */}
          {dispatch.dispatchNotes && (
            <InfoPanel title="Dispatch Notes" icon={FileText}>
              <p className="text-text-secondary whitespace-pre-wrap">
                {dispatch.dispatchNotes}
              </p>
            </InfoPanel>
          )}
        </div>
      </div>

      {/* Complete Delivery Modal */}
      {showStatusModal && (
        <Modal
          title="Complete Delivery"
          onClose={() => setShowStatusModal(false)}
          onConfirm={() => handleStatusUpdate('COMPLETED')}
          confirmText="Complete Delivery"
          isLoading={isUpdating}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
                Actual Quantity Delivered (gallons)
              </label>
              <input
                type="number"
                value={actualQuantity}
                onChange={(e) => setActualQuantity(e.target.value)}
                placeholder={totalOrderedQuantity.toString()}
                className="form-input"
              />
              <p className="text-text-tertiary text-xs mt-1">
                Ordered: {totalOrderedQuantity.toLocaleString()} gallons
              </p>
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
                Signed By
              </label>
              <input
                type="text"
                value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)}
                placeholder="Customer name"
                className="form-input"
              />
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
                Delivery Notes
              </label>
              <textarea
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                rows={3}
                placeholder="Any notes about the delivery..."
                className="form-input resize-none"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <Modal
          title="Cancel Dispatch"
          onClose={() => setShowCancelModal(false)}
          onConfirm={() => handleStatusUpdate('CANCELLED')}
          confirmText="Cancel Dispatch"
          confirmColor="error"
          isLoading={isUpdating}
        >
          <p className="text-text-secondary mb-4">
            Are you sure you want to cancel this dispatch? The order will be returned to CONFIRMED
            status.
          </p>
          <div>
            <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
              Cancellation Reason
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Why is this dispatch being cancelled?"
              className="form-input resize-none"
            />
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <Modal
          title="Delete Dispatch"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          confirmText="Delete Permanently"
          confirmColor="error"
          isLoading={isUpdating}
        >
          <div className="flex items-start space-x-4">
            <AlertTriangle className="w-12 h-12 text-error-red flex-shrink-0" />
            <div>
              <p className="text-text-primary mb-2">
                Are you sure you want to delete dispatch{' '}
                <span className="text-amber font-mono">{dispatch.dispatchNumber}</span>?
              </p>
              <p className="text-text-secondary text-sm">
                This action cannot be undone. The associated order will be returned to CONFIRMED
                status.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

function InfoPanel({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-4">
          <Icon className="w-5 h-5 text-amber" />
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

function TimeStampCard({ label, time }: { label: string; time: string | null }) {
  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div>
      <p className="text-text-tertiary text-xs uppercase font-mono mb-1">{label}</p>
      <p className={`font-mono text-lg ${time ? 'text-text-primary' : 'text-text-tertiary'}`}>
        {formatTime(time)}
      </p>
    </div>
  );
}

function Modal({
  title,
  onClose,
  onConfirm,
  confirmText,
  confirmColor = 'amber',
  isLoading,
  children,
}: {
  title: string;
  onClose: () => void;
  onConfirm: () => void;
  confirmText: string;
  confirmColor?: 'amber' | 'error';
  isLoading: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-panel-dark border border-panel-border p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-display text-text-primary uppercase tracking-wide mb-4">
          {title}
        </h3>
        {children}
        <div className="flex justify-end space-x-4 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-6 py-2 font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 ${
              confirmColor === 'error'
                ? 'bg-error-red hover:bg-error-red/90 text-white'
                : 'bg-amber hover:bg-amber/90 text-background'
            }`}
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DispatchDetailPage;
